import { CSSInterpolation } from '@emotion/css/create-instance'
import { cx } from '@emotion/css'
import React, { type ComponentProps } from 'react'

import { ExtendedPaletteColorName } from '../../shared/MobiusColors'
import type { SemanticColorToken } from '../themes'
import { NeutralColorToken, type ThemeColorToken } from '../themes'
import { BaseButton } from './BaseButton'
import { FoundationIcon } from './FoundationIcon'
import type { Icon16 } from './FoundationIcon/icons/directory'
import { HoverState } from './HoverState'
import { TextColorOverride } from './TextColorOverride'
import { TooltipTrigger } from './TooltipTrigger'
import { Type } from './Type'

const DEFAULT_COLORS = {
  alert: {
    bg: ExtendedPaletteColorName.Lumen,
    fg: ExtendedPaletteColorName.Jet,
  },
  custom: {
    bg: undefined,
    fg: undefined,
  },
  inform: {
    bg: NeutralColorToken.Fg1,
    fg: NeutralColorToken.Bg1,
  },
  inline: {
    bg: NeutralColorToken.Bg2,
    fg: NeutralColorToken.Fg1,
  },
}

const DEFAULT_ICON = {
  alert: 'TriangleAlert16',
  custom: null,
  inform: 'InfoFilled16',
  inline: 'InfoFilled16',
} as const

type BaseProps = {
  /**
   * The text to show in the InfoTag.
   */
  text: string
  /**
   * The state of the InfoTag, if you want to disable it.
   */
  state?: 'default' | 'disabled'
  /**
   * Callback to call when the InfoTag is clicked. If not provided, the InfoTag will not
   * be interactive.
   */
  onClick?: () => void
  /**
   * TooltipTrigger props, if this is truthy InfoTag will be wrapped with TooltipTrigger
   */
  tooltipTriggerProps?: Omit<
    ComponentProps<typeof TooltipTrigger>,
    'renderTrigger'
  >
  /**
   * Pass a CSS object if you need to override styles on the root element. Please do this sparingly.
   */
  overrideRootStyles?: CSSInterpolation
}

type Props = BaseProps &
  (
    | {
        type: 'alert'
        backgroundColor?: never
        foregroundColor?: never
        icon?: never
      }
    | {
        type: 'inform' | 'inline'
        backgroundColor?: never
        foregroundColor?: never
        // TODO(eric.lovett): Remove this prop and convert any uses of it to `type="custom"`.
        icon?: Icon16
      }
    | {
        type: 'custom'
        /**
         * The background color for the InfoTag.
         */
        backgroundColor: SemanticColorToken | ThemeColorToken
        /**
         * The foreground color for the InfoTag, used for the text and any icon.
         */
        foregroundColor: SemanticColorToken | ThemeColorToken
        /**
         * Optionally specify a custom icon to be used. If omitted, no icon will be displayed.
         */
        icon?: Icon16
      }
  )

/**
 * An `InfoTag` is a condensed form factor for displaying bite-sized data or other important
 * information. They can be prominently positioned on the screen to alert (`type="alert"`)
 * or inform (`type="inform"`), or can serve as a subtle inline callout within rows or other
 * repeated modules (`type="inline"`), or can be wholly custom (`type="custom"`). They vary
 * in functionality between hover and click depending on the amount of additional education or
 * an explanation needed.
 */
export function InfoTag({
  backgroundColor: backgroundColorProp,
  foregroundColor: foregroundColorProp,
  icon: iconProp,
  onClick,
  overrideRootStyles,
  state = 'default',
  text,
  tooltipTriggerProps,
  type,
}: Props) {
  const isDisabled = state === 'disabled'

  const backgroundColorWhenEnabled =
    backgroundColorProp ?? DEFAULT_COLORS[type].bg
  const backgroundColor = isDisabled
    ? NeutralColorToken.Bg2
    : backgroundColorWhenEnabled

  const foregroundColorWhenEnabled =
    foregroundColorProp ?? DEFAULT_COLORS[type].fg
  const foregroundColor = isDisabled
    ? NeutralColorToken.Fg3
    : foregroundColorWhenEnabled

  const icon = iconProp ?? DEFAULT_ICON[type]

  const contents = (
    <HoverState color={backgroundColor}>
      {({ colorVariable, hoverableClassName, rootClassName }) => (
        <TextColorOverride color={foregroundColor}>
          {({ rootClassName: textColorOverrideRootClassName }) => (
            <div
              className={cx(
                rootClassName,
                onClick && !isDisabled && hoverableClassName,
                textColorOverrideRootClassName
              )}
              css={[
                {
                  alignItems: 'center',
                  backgroundColor: colorVariable,
                  borderRadius: '20px',
                  display: 'inline-flex',
                  padding: '4px 16px 4px 12px',
                  verticalAlign: 'middle',
                },
                !onClick && overrideRootStyles,
              ]}
            >
              {icon && <FoundationIcon type={icon} />}
              <Type.Text
                isBold
                overrideRootStyles={{
                  // Note that we provide padding whether or not there is an icon. When there
                  // is an icon, this provides a bit of space between the icon and text. When
                  // there is not an icon, this adjusts for the difference in padding on the
                  // parent div.
                  paddingLeft: '4px',
                }}
                size="S"
              >
                {text}
              </Type.Text>
            </div>
          )}
        </TextColorOverride>
      )}
    </HoverState>
  )

  if (tooltipTriggerProps && onClick) {
    return (
      <TooltipTrigger
        {...tooltipTriggerProps}
        renderTrigger={({
          'aria-describedby': ariaDescribedBy,
          closeTooltip,
          openTooltip,
        }) => (
          <BaseButton
            aria-describedby={ariaDescribedBy}
            onBlur={closeTooltip}
            onClick={onClick}
            onFocus={openTooltip}
            onMouseEnter={openTooltip}
            onMouseLeave={closeTooltip}
            overrideRootStyles={overrideRootStyles}
            state={state}
            type="button"
          >
            {contents}
          </BaseButton>
        )}
      />
    )
  }

  if (onClick) {
    return (
      <BaseButton
        onClick={onClick}
        overrideRootStyles={overrideRootStyles}
        state={state}
        type="button"
      >
        {contents}
      </BaseButton>
    )
  }

  return contents
}
